import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import logo from "../img/easyILA_Logo.svg";
import logoLight from "../img/easyILA_Logo_White.svg";
import Loader from "./loader";
import CollapseHeaders from "./collapse-header";

const ContactUs = () => {
  const [formData, setFormData] = useState({
    FirstName: "",
    LastName: "",
    Email: "",
    Service: "",
    MobileNo: "",
    DateOfBirth: "",
    Nationality: "",
    Gender: "",
    Message: "",
  });

  const [isChecked, setIsChecked] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");
  const [messageClass, setMessageClass] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [isScrolled, setIsScrolled] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {

    const setMetaTag = (name, content) => {
      let metaTag = document.querySelector(`meta[name='${name}']`);
      if (!metaTag) {
        metaTag = document.createElement("meta");
        metaTag.setAttribute("name", name);
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute("content", content);
    };

    // Set for all major bots
    setMetaTag("robots", "noindex, nofollow");
    setMetaTag("googlebot", "noindex, nofollow");
    setMetaTag("bingbot", "noindex, nofollow");
  }, []);

  useEffect(() => {
    const handlePageLoad = () => {
      setIsLoading(false);
    };

    if (document.readyState === "complete") {
      setIsLoading(false);
    } else {
      window.addEventListener("load", handlePageLoad);
    }

    return () => window.removeEventListener("load", handlePageLoad);
  }, []);

  useEffect(() => {
    const links = document.querySelectorAll(".mobile-menu a");

    const closeMenu = () => {
      const slicknav = document.querySelector(".slicknav_nav");
      if (slicknav) {
        slicknav.style.display = "none";
      }
    };

    links.forEach((link) => link.addEventListener("click", closeMenu));

    return () => {
      links.forEach((link) => link.removeEventListener("click", closeMenu));
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const formatNumber = (value) => {
    return value.replace(/\D/g, "").slice(0, 10);
  };

  const imageHeight = windowWidth <= 992 ? "50px" : "90px";

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Validate as user types
    let error = "";

    if (name === "FirstName" || name === "LastName") {
      if (!/^[A-Za-z ]*$/.test(value)) {
        error = "Only letters are allowed";
      } else if (value.length > 25) {
        error = "Maximum 25 characters allowed";
      }
    } else if (name === "Email") {
      if (value && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)) {
        error = "Please enter a valid email";
      }
    } else if (name === "MobileNo") {
      if (value && !/^[0-9]{0,10}$/.test(value)) {
        error = "Only numbers allowed (max 10 digits)";
      }
    } else if (name === "Message") {
      if (value.length > 200) {
        error = "Maximum 200 characters allowed";
      }
    }

    setErrors({
      ...errors,
      [name]: error,
    });

    setFormData({
      ...formData,
      [name]: name === "MobileNo" ? formatNumber(value) : value,
    });
  };

  const handleDobChange = (e) => {
    let input = e.target.value.replace(/[^0-9/]/g, ""); // Remove non-digits and non-slashes
    input = input.replace(/\/+/g, "/"); // Remove duplicate slashes

    // Auto-insert slashes
    if (input.length > 2 && input.charAt(2) !== "/") {
      input = input.slice(0, 2) + "/" + input.slice(2);
    }
    if (input.length > 5 && input.charAt(5) !== "/") {
      input = input.slice(0, 5) + "/" + input.slice(5);
    }

    // Limit length to 10 characters (MM/DD/YYYY)
    if (input.length > 10) {
      input = input.slice(0, 10);
    }

    // Validate month and day ranges as user types
    const parts = input.split("/");
    if (parts[0] && parseInt(parts[0]) > 12) {
      input = "12" + (parts[1] ? "/" + parts[1] : "");
    }
    if (parts[1] && parseInt(parts[1]) > 31) {
      input = (parts[0] || "") + "/31" + (parts[2] ? "/" + parts[2] : "");
    }

    setFormData({ ...formData, DateOfBirth: input });

    // Only validate format when input is complete (10 characters)
    if (input.length === 10) {
      const dobPattern =
        /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/(19[0-9]{2}|20[0-2][0-9])$/;
      if (!dobPattern.test(input)) {
        const currentYear = new Date().getFullYear();
        const maxYear = currentYear - 1;
        setErrors({
          ...errors,
          DateOfBirth: `Invalid Date Format (MM/DD/YYYY) and date between 1900 and ${maxYear}`,
        });
      } else {
        // Additional validation for the date itself
        const [month, day, year] = input.split("/").map(Number);
        const date = new Date(year, month - 1, day);
        const currentYear = new Date().getFullYear();
        const maxYear = currentYear - 1;

        if (
          date.getFullYear() !== year ||
          date.getMonth() + 1 !== month ||
          date.getDate() !== day ||
          year < 1900 ||
          year > maxYear
        ) {
          setErrors({
            ...errors,
            DateOfBirth: `Please enter a valid date between 1900 and ${maxYear}`,
          });
        } else {
          setErrors({ ...errors, DateOfBirth: "" });
        }
      }
    } else {
      setErrors({ ...errors, DateOfBirth: "" });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // Required fields validation
    if (!formData.FirstName.trim()) {
      newErrors.FirstName = "First Name is required";
      isValid = false;
    } else if (!/^[A-Za-z ]+$/.test(formData.FirstName)) {
      newErrors.FirstName = "Only letters are allowed";
      isValid = false;
    } else if (formData.FirstName.length > 25) {
      newErrors.FirstName = "Maximum 25 characters allowed";
      isValid = false;
    }

    if (!formData.LastName.trim()) {
      newErrors.LastName = "Last Name is required";
      isValid = false;
    } else if (!/^[A-Za-z ]+$/.test(formData.LastName)) {
      newErrors.LastName = "Only letters are allowed";
      isValid = false;
    } else if (formData.LastName.length > 25) {
      newErrors.LastName = "Maximum 25 characters allowed";
      isValid = false;
    }

    if (!formData.Email.trim()) {
      newErrors.Email = "Email is required";
      isValid = false;
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.Email)
    ) {
      newErrors.Email = "Please enter a valid email";
      isValid = false;
    }

    if (!formData.Service) {
      newErrors.Service = "Please select a service";
      isValid = false;
    }

    if (!formData.MobileNo.trim()) {
      newErrors.MobileNo = "Mobile number is required";
      isValid = false;
    } else if (!/^[0-9]{10}$/.test(formData.MobileNo)) {
      newErrors.MobileNo = "Please enter a valid 10-digit number";
      isValid = false;
    }

    if (!formData.DateOfBirth.trim()) {
      newErrors.DateOfBirth = "Date of Birth is required";
      isValid = false;
    } else if (
      !/^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/(19[0-9]{2}|20[0-2][0-9])$/.test(
        formData.DateOfBirth
      )
    ) {
      newErrors.DateOfBirth =
        "Please enter a valid date (MM/DD/YYYY) between 1900 and current year";
      isValid = false;
    }

    if (!isChecked) {
      newErrors.terms = "You must accept the terms and conditions";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setSubmitMessage("Please correct the errors in the form");
      setMessageClass("text-danger");
      return;
    }

    setLoading(true); // Show loader

    const submissionData = {
      ...formData,
      Gender: formData.Gender || "NA",
      Message: formData.Message || "NA",
      Nationality: formData.Nationality || "NA",
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/submitInquiry`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(submissionData),
        }
      );
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      // Update success message
      setSubmitMessage(
        "Your form has been submitted successfully! Redirecting..."
      );

      // Set success class
      setMessageClass("text-success");

      // Reset form
      setFormData({
        FirstName: "",
        LastName: "",
        Email: "",
        Service: "",
        MobileNo: "",
        DateOfBirth: "",
        Nationality: "",
        Gender: "",
        Message: "",
      });
      setIsChecked(false);

      window.location.href = "/thankyou";
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitMessage("Failed to submit form. Please try again later.");
      setMessageClass("text-danger");
    } finally {
      setLoading(false); // Hide loader
      setTimeout(() => {
        setSubmitMessage("");
      }, 5000);
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <CollapseHeaders />

      <section
        id="contact"
        className="section"
        data-stellar-background-ratio="-0.2"
        style={{ paddingTop: "7%" }}
      >
        <div className="contact-form">
          <div className="container">
            <div className="contact-info-enquiry">
              <h3>Please submit the following information</h3>
            </div>
            <div className="contact-content-enquiry">
              <form id="contactForm" onSubmit={handleSubmit}>
                <div className="row">
                  {/* First & Last Name */}
                  <div
                    className="col-md-12"
                    style={{
                      display: "flex",
                      gap: "10px",
                    }}
                  >
                    <div className="form-group" style={{ width: "100%" }}>
                      <input
                        type="text"
                        className="form-control-enquiry"
                        name="FirstName"
                        placeholder="First Name *"
                        value={formData.FirstName}
                        onChange={handleChange}
                        maxLength="25"
                      />
                      {errors.FirstName && (
                        <small className="text-danger">
                          {errors.FirstName}
                        </small>
                      )}
                    </div>
                    <div className="form-group" style={{ width: "100%" }}>
                      <input
                        type="text"
                        className="form-control-enquiry"
                        name="LastName"
                        placeholder="Last Name *"
                        value={formData.LastName}
                        onChange={handleChange}
                        maxLength="25"
                      />
                      {errors.LastName && (
                        <small className="text-danger">{errors.LastName}</small>
                      )}
                    </div>
                  </div>

                  {/* Email */}
                  <div className="col-md-12">
                    <div className="form-group">
                      <input
                        type="email"
                        placeholder="Email *"
                        className="form-control-enquiry"
                        name="Email"
                        value={formData.Email}
                        onChange={handleChange}
                      />
                      {errors.Email && (
                        <small className="text-danger">{errors.Email}</small>
                      )}
                    </div>
                  </div>

                  {/* Services */}
                  <div className="col-md-12">
                    <div className="form-group">
                      <select
                        className="form-control-enquiry"
                        name="Service"
                        value={formData.Service}
                        onChange={handleChange}
                        style={{
                          height: "54px",
                          appearance: "none",
                          WebkitAppearance: "none",
                          MozAppearance: "none",
                        }}
                      >
                        <option
                          value=""
                          style={{
                            color: "black",
                          }}
                        >
                          Select a Service *
                        </option>
                        <option
                          value="Green Card Renewal Assistance"
                          style={{
                            color: "black",
                          }}
                        >
                          Green Card Renewal Assistance
                        </option>
                        <option
                          value="U.S. Citizenship (Naturalization)"
                          style={{
                            color: "black",
                          }}
                        >
                          U.S. Citizenship (Naturalization)
                        </option>
                        <option
                          value="EB5 Services"
                          style={{
                            color: "black",
                          }}
                        >
                          EB5 Services
                        </option>
                        <option
                          value="Family-Based Green Card"
                          style={{
                            color: "black",
                          }}
                        >
                          Family-Based Green Card
                        </option>
                      </select>
                      {errors.Service && (
                        <small className="text-danger">{errors.Service}</small>
                      )}
                    </div>
                  </div>

                  {/* Mobile & DOB */}
                  <div
                    className="col-md-12"
                    id="mobileForm"
                    style={{
                      display: "flex",
                      gap: "10px",
                    }}
                  >
                    <div className="form-group" style={{ width: "100%" }}>
                      <input
                        type="tel"
                        placeholder="Mobile No. *"
                        className="form-control-enquiry"
                        name="MobileNo"
                        maxLength="10"
                        value={formData.MobileNo}
                        onChange={handleChange}
                      />
                      {errors.MobileNo && (
                        <small className="text-danger">{errors.MobileNo}</small>
                      )}
                    </div>
                    <div className="form-group" style={{ width: "100%" }}>
                      <input
                        type="text"
                        placeholder="Date of Birth (MM/DD/YYYY) *"
                        className="form-control-enquiry"
                        name="DateOfBirth"
                        value={formData.DateOfBirth}
                        onChange={handleDobChange}
                      />
                      {errors.DateOfBirth && (
                        <small className="text-danger">
                          {errors.DateOfBirth}
                        </small>
                      )}
                    </div>
                  </div>

                  {/* Nationality & Gender */}
                  <div
                    className="col-md-12"
                    id="mobileForm"
                    style={{
                      display: "flex",
                      gap: "10px",
                    }}
                  >
                    <div className="form-group" style={{ width: "100%" }}>
                      <input
                        type="text"
                        placeholder="Current Citizenship"
                        className="form-control-enquiry"
                        name="Nationality"
                        value={formData.Nationality}
                        onChange={handleChange}
                        style={{
                          height: "54px",
                          appearance: "none",
                          WebkitAppearance: "none",
                          MozAppearance: "none",
                        }}
                      />
                    </div>
                    <div className="form-group" style={{ width: "100%" }}>
                      <select
                        className="form-control-enquiry"
                        name="Gender"
                        value={formData.Gender}
                        onChange={handleChange}
                        style={{
                          height: "54px",
                          appearance: "none",
                          WebkitAppearance: "none",
                          MozAppearance: "none",
                        }}
                      >
                        <option
                          value=""
                          style={{
                            color: "black",
                          }}
                        >
                          Select Gender
                        </option>
                        <option
                          value="Male"
                          style={{
                            color: "black",
                          }}
                        >
                          Male
                        </option>
                        <option
                          value="Female"
                          style={{
                            color: "black",
                          }}
                        >
                          Female
                        </option>
                      </select>
                    </div>
                  </div>

                  {/* Message */}
                  <div className="col-md-12">
                    <div className="form-group">
                      <textarea
                        className="form-control-enquiry"
                        name="Message"
                        placeholder="Message"
                        rows="4"
                        value={formData.Message}
                        onChange={handleChange}
                        maxLength="200"
                      ></textarea>
                      <small
                        style={{
                          display: "block",
                          textAlign: "right",
                        }}
                      >
                        {formData.Message.length}
                        /200 characters
                      </small>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <label
                        style={{
                          textAlign: "center",
                        }}
                        className="ptd-lable"
                      >
                        <input
                          style={{
                            marginRight: "10px",
                          }}
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => setIsChecked(e.target.checked)}
                        />
                        I have read and agree to the
                        <Link
                          style={{
                            margin: "0 5px",
                          }}
                          to="/termsandconditions"
                        >
                          Terms & Conditions
                        </Link>
                        ,
                        <Link
                          style={{
                            margin: "0 5px",
                          }}
                          to="/privacypolicy"
                        >
                          Privacy Policy
                        </Link>
                        and{" "}
                        <Link
                          style={{
                            margin: "0 5px",
                          }}
                          to="/disclaimer"
                        >
                          Disclaimer
                        </Link>
                        . We respact your privacy and your data is encrypted.
                      </label>
                      {errors.terms && (
                        <p
                          style={{
                            color: "red",
                            textAlign: "center",
                            marginTop: "5px",
                          }}
                        >
                          {errors.terms}
                        </p>
                      )}
                    </div>
                    <div className="submit-button text-center">
                      <button
                        className="btn btn-common"
                        type="submit"
                        disabled={loading}
                        style={{ marginTop: "20px" }}
                      >
                        {loading ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm"
                              role="status"
                              aria-hidden="true"
                            ></span>{" "}
                            Sending...
                          </>
                        ) : (
                          "Send Message"
                        )}
                      </button>
                      {submitMessage && (
                        <div
                          className={messageClass}
                          style={{
                            marginTop: "15px",
                          }}
                        >
                          {submitMessage}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginTop: "16px",
          }}
        ></div>
      </section>
      <footer>
        <div className="container">
          <div className="row">
            <p
              style={{
                width: "100%",
                textAlign: "center",
                color: "#fff",
                fontSize: "14px",
                fontWeight: "400",
                padding: "0 20px",
              }}
            >
              DISCLAIMER IN PART: (Read full Disclaimer here): This site is not
              a substitute for hiring an attorney or law firm and does not
              provide legal advice. This site does not endorse or recommend any
              participating third-party legal professionals that pay to
              participate in advertising. An attorney-client relationship is not
              formed when calling the number on this site or filling out a form.
              Services may not be available for all legal categories. All
              persons depicted in a photo or video are actors or models or from
              unsplash.com and not clients of any firm. WE ARE NOT A LEGAL FIRM
              OR AN ACCREDITED ORGANIZATION. ALL IMMIGRATION MATTERS SHOULD BE
              CONSULTED WITH IMMIGRATION ATTORNEYS OR AS GUIDED BY USCIS FROM
              TIME TO TIME. WE DO NOT REPRESENT USCIS. WE DO NOT GIVE ANY
              ADVICE, OPINION, OR CONSULTANCY ON IMMIGRATION MATTERS.
            </p>
            <div className="col-lg-6 col-sm-6 col-xs-12">
              <ul className="footer-links-enquiry">
                <li>
                  <Link to="/">Home</Link>
                </li>
                <li>
                  <Link to="/privacypolicy">Privacy Policy</Link>
                </li>
              </ul>
            </div>
            <div className="col-lg-6 col-sm-6 col-xs-12">
              <div className="copyright-enquiry">
                <p>All copyrights reserved &copy; 2025 - easyILA</p>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default ContactUs;
